import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  DossierDetailPatch,
  MunicipalityDossier,
} from '@api/models/dossier.model';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { catchError, EMPTY, finalize, forkJoin, of, tap } from 'rxjs';
import { AppService } from '../../app.service';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { CardComponent } from '@ui/card/card.component';
import { IndexResponse } from '@api/models/response.model';
import { Router } from '@angular/router';
import { ButtonComponent } from '@ui/button/button.component';
import { FormsModule } from '@angular/forms';
import { InputComponent } from '@ui/input/input.component';
import { SelectComponent } from '@ui/select/select.component';
import { NotificationService } from '@ui/notification/notification.service';
import { CreateRequestModalComponent } from '@features/requests/create-request-modal/create-request-modal.component';
import { ModalService } from '@ui/modal/modal.service';

interface DossierFilters {
  q: string;
  'afas_data.name': string;
  'afas_data.address_zipcode': string;
  /**
   * Standaard uit: `filter[showClosed]=true` → inactieve opdrachten ook tonen.
   * Aan (“alleen actieve”): geen `showClosed` → backend sluit inactieve opdrachten uit.
   */
  onlyActiveAssignments: boolean;
  /** Leeg = geen filter; zie filter[status] in API-docs (komma’s voor meerdere waarden op BE). */
  status: string;
}

@Component({
  selector: 'app-assignments',
  standalone: true,
  imports: [
    TableComponent,
    CardComponent,
    ButtonComponent,
    FormsModule,
    InputComponent,
    SelectComponent,
  ],
  template: `
    <div
      class="flex flex-col gap-4 h-[calc(100vh-theme(spacing.24))] overflow-hidden"
    >
      <ui-card>
        <div class="flex justify-end items-center mb-4">
          <ui-button
            [icon]="'add'"
            (onClick)="addRequest()"
            [disabled]="!municipalityId()"
            >Nieuwe aanvraag</ui-button
          >
        </div>
        <div class="flex flex-col gap-4 justify-between">
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4"
          >
            <ui-input
              label="Zoeken"
              placeholder="WMO-referentie of klantnummer..."
              [(ngModel)]="filters.q"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <ui-input
              label="Naam"
              placeholder="Zoek op naam..."
              [(ngModel)]="filters['afas_data.name']"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <ui-input
              label="Postcode"
              placeholder="Zoek op postcode..."
              [(ngModel)]="filters['afas_data.address_zipcode']"
              (ngModelChange)="onFilterChange()"
            ></ui-input>

            <ui-select
              label="Status"
              [placeholder]="'Alle statussen'"
              [options]="statusFilterOptions"
              [(ngModel)]="filters.status"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onFilterChange()"
            ></ui-select>

            <ui-input
              label="Toon alleen actieve opdrachten"
              type="checkbox"
              [(ngModel)]="filters.onlyActiveAssignments"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="applyFilters()"
            ></ui-input>
          </div>
          <div class="flex gap-2 items-end" style="padding-bottom: 2px;">
            <ui-button
              variant="primary"
              [icon]="'filter'"
              (onClick)="applyFilters()"
              >Filter toepassen</ui-button
            >
            <ui-button
              variant="ghost"
              [icon]="'refresh'"
              (onClick)="resetFilters()"
              >Reset filters</ui-button
            >
          </div>
        </div>
      </ui-card>
      <div class="h-full overflow-hidden">
        <ui-table
          [columns]="columns"
          [data]="dossiers()"
          [loading]="loading()"
          (onRowClick)="onRowClick($event)"
          (onPageChange)="onPageChange($event)"
          [showPagination]="true"
          [meta]="meta()"
          [bordered]="false"
        ></ui-table>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentsComponent {
  municipalitiesService = inject(MunicipalitiesService);
  appService = inject(AppService);
  router = inject(Router);
  notificationService = inject(NotificationService);
  modalService = inject(ModalService);

  municipalityId = this.appService.municipalityId;
  dossiers = signal<MunicipalityDossier[]>([]);
  meta = signal<IndexResponse<MunicipalityDossier>['meta'] | null>(null);
  loading = signal<boolean>(false);

  /** Voorkomt dat een langzame batch-details-response een nieuwere pagina overschrijft */
  private listFetchGeneration = 0;

  columns: TableColumn[] = [
    {
      key: 'number',
      label: 'Nummer',
      width: '100px',
      format: (value: string | null) => (value != null && value !== '' ? value : '—'),
    },
    {
      key: 'wmo_reference',
      label: 'WMO referentie',
      width: '150px',
      format: (value: string | null) => (value != null && value !== '' ? value : '—'),
    },
    {
      key: 'customer_name',
      label: 'Klantnaam',
      width: '200px',
      cellPending: (row: MunicipalityDossier) =>
        row.item_type === 'request' && row.customer_name == null,
      format: (value: string | null) =>
        value != null && value !== '' ? value : '—',
    },
    {
      key: 'address',
      label: 'Adres',
      width: '300px',
      cellPending: (row: MunicipalityDossier) =>
        row.item_type === 'request' && row.address == null,
      format: (value: string | null) =>
        value != null && value !== '' ? value : '—',
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      format: (value: string | null) =>
        value != null && value !== '' ? value : '—',
    },
    {
      key: 'has_customer_account',
      label: 'App-account',
      width: '110px',
      format: (value: boolean | null | undefined) => (value ? 'Ja' : 'Nee'),
    },
    {
      key: 'created_at',
      label: 'Aangemaakt op',
      width: '150px',
      align: 'right',
      format: (value: string) =>
        new Date(value).toLocaleString('nl-NL', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
  ];

  readonly statusFilterOptions = [
    { label: 'Adviesfase', value: 'Adviesfase' },
    { label: 'In uitvoering', value: 'In uitvoering' },
    { label: 'Afgerond', value: 'Afgerond' },
  ];

  filters: DossierFilters = {
    q: '',
    'afas_data.name': '',
    'afas_data.address_zipcode': '',
    onlyActiveAssignments: false,
    status: '',
  };

  constructor() {
    effect(
      () => {
        if (this.appService.municipalityId()) {
          this.fetchDossiers();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.fetchDossiers();
  }

  onPageChange(page: number) {
    this.fetchDossiers(page);
  }

  private buildFilterRecord(): Record<string, string | boolean> {
    const f = this.filters;
    const record: Record<string, string | boolean> = {};
    if (f['afas_data.name']) {
      record['afas_data.name'] = f['afas_data.name'];
    }
    if (f['afas_data.address_zipcode']) {
      record['afas_data.address_zipcode'] = f['afas_data.address_zipcode'];
    }
    if (f.status) {
      record['status'] = f.status;
    }
    if (!f.onlyActiveAssignments) {
      record['showClosed'] = true;
    }
    return record;
  }

  private dossierRowKey(row: Pick<MunicipalityDossier, 'item_type' | 'id'>): string {
    return `${row.item_type}:${row.id}`;
  }

  private mergeDossierPatches(
    rows: MunicipalityDossier[],
    patches: DossierDetailPatch[]
  ): MunicipalityDossier[] {
    const patchMap = new Map(
      patches.map((p) => [this.dossierRowKey(p), p])
    );
    return rows.map((row) => {
      const p = patchMap.get(this.dossierRowKey(row));
      if (!p) return row;
      return {
        ...row,
        customer_name: p.customer_name ?? row.customer_name,
        address: p.address ?? row.address,
        status: p.status ?? row.status,
      };
    });
  }

  /**
   * Haalt naam/adres/status batchgewijs op (max 50 per request), parallel per chunk.
   */
  private loadDossierDetailsAsync(rows: MunicipalityDossier[], fetchGeneration: number): void {
    if (!rows.length) return;

    const needsBatch = rows.some(
      (r) =>
        r.item_type === 'request' &&
        (r.customer_name == null || r.address == null)
    );
    if (!needsBatch) return;

    const items = rows
      .filter(
        (r) =>
          r.item_type === 'request' &&
          (r.customer_name == null || r.address == null)
      )
      .map((r) => ({ item_type: r.item_type, id: r.id }));
    const chunks: Array<{ item_type: MunicipalityDossier['item_type']; id: string }[]> =
      [];
    for (let i = 0; i < items.length; i += 50) {
      chunks.push(items.slice(i, i + 50));
    }

    forkJoin(
      chunks.map((chunk) =>
        this.municipalitiesService.postDossiersDetails(chunk).pipe(
          catchError((err) => {
            console.error(err);
            return of({ data: [] as DossierDetailPatch[] });
          })
        )
      )
    ).subscribe((responses) => {
      if (fetchGeneration !== this.listFetchGeneration) return;
      const patches = responses.flatMap((r) => r.data);
      this.dossiers.update((current) =>
        this.mergeDossierPatches(current, patches)
      );
    });
  }

  private fetchDossiers(page: number = 1, perPage: number = 50) {
    const fetchGeneration = ++this.listFetchGeneration;
    this.loading.set(true);
    try {
      this.municipalitiesService
        .getDossiers(page, perPage, this.buildFilterRecord(), this.filters.q)
        .pipe(
          tap(() => this.loading.set(true)),
          catchError((error) => {
            console.error(error);
            this.loading.set(false);
            return EMPTY;
          }),
          finalize(() => this.loading.set(false))
        )
        .subscribe((response) => {
          if (fetchGeneration !== this.listFetchGeneration) return;
          this.dossiers.set(response.data);
          this.meta.set(response.meta);
          this.loadDossierDetailsAsync(response.data, fetchGeneration);
        });
    } catch (error: any) {
      this.notificationService.error(error.message);
      this.loading.set(false);
    }
  }

  onRowClick(row: MunicipalityDossier) {
    if (row.item_type === 'request') {
      this.router.navigate(['/assignments', 'request', row.id]);
      return;
    }
    this.router.navigate(['/assignments', row.id]);
  }

  onFilterChange() {
    // Optioneel: automatisch filteren bij elke wijziging
    //this.applyFilters();
  }

  applyFilters() {
    this.fetchDossiers(1, 50);
  }

  addRequest() {
    const modalRef = this.modalService.open(CreateRequestModalComponent, {
      data: { initialRequestType: 'request' as const },
    });
    modalRef.onClose.subscribe(() => {
      this.fetchDossiers(1);
    });
  }

  resetFilters() {
    this.filters = {
      q: '',
      'afas_data.name': '',
      'afas_data.address_zipcode': '',
      onlyActiveAssignments: false,
      status: '',
    };
    this.fetchDossiers(1);
  }
}
